import React, { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { SlideupDialog, Dropdown, DropdownButtonItem } from '@ygtechnologies/bygr-frontend-component-library';
import theme from './DocumentListSlideup.module.scss';
import { BaseDocument, ProjectDocument, ResidentialUnitDocument } from '@/types';
import { Button } from '@ygtechnologies/rubus-ui';
import DocumentApproveMultipleDialog from '../multiple/DocumentApproveMultipleDialog';
// import DocumentRejectMultipleDialog from "../multiple/DocumentRejectMultipleDialog"; // Temporarily commented out
import { useIsMobile } from '@/utils/hooks';
import { faChevronDown, faCheck /*, faTimes*/ } from '@fortawesome/pro-light-svg-icons';
import { uniq } from 'lodash-es';

interface DocumentListSlideupProps {
    isOpen: boolean;
    onCloseDialog: () => void;
    selectedDocuments: (BaseDocument | ProjectDocument)[];
    onChange?: () => void;
}

export default function DocumentListSlideup({
    isOpen,
    onCloseDialog,
    selectedDocuments,
    onChange,
}: DocumentListSlideupProps) {
    const [showApproveDialog, setShowApproveDialog] = useState(false);
    // const [showRejectDialog, setShowRejectDialog] = useState(false); // Temporarily commented out
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const isMobile = useIsMobile();
    // Extract unique residential unit IDs from selected documents
    const residentialUnitIds = uniq(
        selectedDocuments
            .filter(doc => 'residentialUnitId' in doc && (doc as any).residentialUnitId)
            .map(doc => (doc as ResidentialUnitDocument).residentialUnitId)
    );


    const handleApprove = () => {
        setDropdownOpen(false);
        setShowApproveDialog(true);
    };

    /* Temporarily commented out
    const handleReject = () => {
        setDropdownOpen(false);
        setShowRejectDialog(true);
    }
    */

    const unpublishedDocuments = useMemo(() => {
        return selectedDocuments.filter((doc) => doc.unpublishedVersionsCount >= 1);
    }, [selectedDocuments]);

    const renderMobileActions = () => (
        <div className={theme.mobileDropdownWrapper}>
            <Dropdown
                variant="secondary"
                icon={faChevronDown}
                toggle={setDropdownOpen}
                open={dropdownOpen}
                label={<Trans>Select action</Trans>}>
                <DropdownButtonItem
                    icon={faCheck}
                    onClick={() => {
                        setDropdownOpen(false);
                        handleApprove();
                    }}>
                    <Trans>Publish {unpublishedDocuments.length} document{unpublishedDocuments.length !== 1 ? 's' : ''}</Trans>
                </DropdownButtonItem>
                {/* Temporarily hide reject option - to be re-enabled later
                <DropdownButtonItem 
                    icon={faTimes} 
                    onClick={() => {
                        setDropdownOpen(false);
                        handleReject();
                    }}>
                    <Trans>Reject {unpublishedDocuments.length} document{unpublishedDocuments.length !== 1 ? 's' : ''}</Trans>
                </DropdownButtonItem>
                */}
            </Dropdown>
        </div>
    );

    const renderDesktopActions = () => (
        <div className={theme.actionButtons}>
            <Button variant="tertiary" onClick={handleApprove} disabled={unpublishedDocuments.length === 0}>
                <Trans>Publish {unpublishedDocuments.length} document{unpublishedDocuments.length !== 1 ? 's' : ''}</Trans>
            </Button>
            {/* Temporarily hide reject button - to be re-enabled later
            <Button variant="tertiary" onClick={handleReject} disabled={unpublishedDocuments.length === 0}>
                <Trans>
                    Reject {unpublishedDocuments.length} document{unpublishedDocuments.length !== 1 ? 's' : ''}
                </Trans>
            </Button>
            */}
        </div>
    );

    const renderNoActionsMessage = () => (
        <span className={theme.noUnpublishedDocuments}>
            <Trans>No unpublished documents. No action available.</Trans>
        </span>
    );

    const renderDialogContent = () => {
        if (unpublishedDocuments.length === 0) return renderNoActionsMessage();
        if (isMobile) return renderMobileActions();
        return renderDesktopActions();
    };

    return (
        <>
            <SlideupDialog
                isOpen={isOpen}
                onCloseDialog={onCloseDialog}
                heading={<Trans>Selected documents: {selectedDocuments.length}</Trans>}
                className={`${theme.selectedDocumentsSlideupDialog} fix-slide-up-position`}
            >
                <div className={theme.selectedDocumentsSlideupDialogContent}>
                    {renderDialogContent()}
                </div>
            </SlideupDialog>

            <DocumentApproveMultipleDialog
                show={showApproveDialog}
                onClose={() => setShowApproveDialog(false)}
                documents={selectedDocuments}
                residentialUnitIds={residentialUnitIds}
                onApprove={() => {
                    onChange?.();
                    onCloseDialog();
                }}
                hiddenFromBuyer={false}
            />

            {/* Temporarily hide reject dialog - to be re-enabled later
            <DocumentRejectMultipleDialog
                show={showRejectDialog}
                onClose={() => setShowRejectDialog(false)}
                documents={selectedDocuments}
                residentialUnitIds={residentialUnitIds}
                onReject={() => {
                    onChange?.();
                    onCloseDialog();
                }}
            />
            */}
        </>
    );
}
